import React, { useEffect, useRef, useState, useCallback } from "react";
import Map from "ol/Map.js";
import View from "ol/View.js";
import TileLayer from "ol/layer/Tile.js";
import OSM from "ol/source/OSM";
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import ClusterSource from 'ol/source/Cluster';
import GeoJSON from 'ol/format/GeoJSON';
import Overlay from 'ol/Overlay';
import { defaults as defaultControls } from 'ol/control';
import { useGeographic } from 'ol/proj';
import { Style, Fill, Stroke, Circle, Text } from 'ol/style';
import "ol/ol.css";

const AREAS_RESPONSABLES = [
    { value: 'todas', label: 'Todas las áreas' },
    { value: 'ÁREA DE MICROCRÉDITOS', label: 'Microcréditos' },
    { value: 'ÁREA DE OBRAS', label: 'Obras' },
    { value: 'ÁREA DE MEJORAS', label: 'Mejoras' },
    { value: 'ÁREA DE INTERVENCIÓN SOCIAL', label: 'Intervención Social' },
    { value: 'ÁREA DE CAPACITACIONES', label: 'Capacitaciones' },
    { value: 'ÁREA DE PROYECTOS', label: 'Proyectos' },
    { value: 'OTRA', label: 'Otra' },
];

const colorPalette = [
    '#e6194B', '#3cb44b', '#ffe119', '#4363d8', '#f58231',
    '#911eb4', '#42d4f4', '#f032e6', '#bfef45', '#fabed4',
    '#469990', '#dcbeff', '#9A6324', '#fffac8', '#800000',
    '#aaffc3', '#808000', '#ffd8b1', '#000075', '#a9a9a9',
];

const getColorForObra = (obraId, index) => {
    const hash = obraId.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
    return colorPalette[hash % colorPalette.length];
};

const MapasPage = () => {
    useGeographic();
    const mapRef = useRef(null);
    const [map, setMap] = useState(null);
    const [areaFilter, setAreaFilter] = useState('todas');
    const [features, setFeatures] = useState([]);
    const [loading, setLoading] = useState(true);
    const [layerVisibility, setLayerVisibility] = useState({
        partido: true,
        geometrias: true,
        beneficiarios: true,
    });
    const geometriasSourceRef = useRef(new VectorSource());
    const beneficiariosSourceRef = useRef(new VectorSource());
    const popupRef = useRef(null);
    const popupContentRef = useRef(null);
    const overlayRef = useRef(null);
    const areaFilterRef = useRef(areaFilter);
    const searchInputRef = useRef(null);

    const handleSearchKeyDown = useCallback(async (e) => {
        if (e.key !== 'Enter' || !searchInputRef.current) return;
        const expediente = searchInputRef.current.value.trim();
        if (!expediente) return;
        try {
            const rawToken = localStorage.getItem('token');
            const token = rawToken ? JSON.parse(rawToken) : null;
            const response = await fetch(`/api/verificar-expediente/${encodeURIComponent(expediente)}`, {
                headers: {
                    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                }
            });
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const result = await response.json();
            if (result.existe && result.data?.[0]) {
                const obraId = result.data[0].cg_01_id_partido_obra;
                // Buscar feature en ambas sources
                let targetFeature = null;
                [...geometriasSourceRef.current.getFeatures(), ...beneficiariosSourceRef.current.getFeatures()].some(f => {
                    if (f.get('obra_id') === obraId) {
                        targetFeature = f;
                        return true;
                    }
                    return false;
                });
                if (targetFeature && targetFeature.getGeometry()) {
                    map?.getView().fit(targetFeature.getGeometry().getExtent(), {
                        padding: [100, 100, 100, 100], duration: 500, maxZoom: 19,
                    });
                    // Mostrar popup con info
                    const props = targetFeature.getProperties();
                    popupContentRef.current.innerHTML = `
                        <div class="text-sm">
                            <div class="font-semibold text-base mb-1">${props.nombre_obra || result.data[0].cg_02_nombre_obra}</div>
                            <div class="text-gray-600"><span class="font-medium">ID:</span> ${obraId}</div>
                        </div>
                    `;
                    const geom = targetFeature.getGeometry();
                    overlayRef.current?.setPosition(geom.getClosestPoint(geom.getFlatCoordinates ? geom.getFlatCoordinates() : undefined));
                }
            }
        } catch (error) {
            console.error('Error searching expediente:', error);
        }
    }, [map, overlayRef]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetch('/api/mapas/todos');
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const geojson = await response.json();
            setFeatures(geojson.features || []);
        } catch (error) {
            console.error('Error fetching map data:', error);
            setFeatures([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        areaFilterRef.current = areaFilter;
        geometriasSourceRef.current?.changed();
        beneficiariosSourceRef.current?.changed();
    }, [areaFilter]);

    useEffect(() => {
        if (!mapRef.current) return;

        const overlay = new Overlay({
            element: popupRef.current,
            positioning: 'bottom-center',
            offset: [0, -10],
            autoPan: true,
            autoPanAnimation: { duration: 250 },
        });
        const dimStyle = new Style({
            fill: new Fill({ color: 'rgba(200,200,200,0.15)' }),
            stroke: new Stroke({ color: '#ccc', width: 1 }),
            image: new Circle({
                radius: 4,
                fill: new Fill({ color: '#ccc' }),
                stroke: new Stroke({ color: '#eee', width: 1 }),
            }),
        });
        const dimBeneficiariosStyle = new Style({
            image: new Circle({
                radius: 4,
                fill: new Fill({ color: '#ccc' }),
                stroke: new Stroke({ color: '#eee', width: 1 }),
            }),
        });

        const geometriasLayer = new VectorLayer({
            source: geometriasSourceRef.current,
            style: (feature) => {
                const areaFilterVal = areaFilterRef.current;
                if (areaFilterVal !== 'todas' && feature.get('__area') !== areaFilterVal) {
                    return dimStyle;
                }
                const obraId = feature.get('obra_id');
                const color = getColorForObra(obraId || '');
                const geomType = feature.getGeometry()?.getType();
                return new Style({
                    fill: new Fill({ color: color + '33' }),
                    stroke: new Stroke({ color, width: 2 }),
                    image: geomType === 'Point' ? new Circle({
                        radius: 6,
                        fill: new Fill({ color }),
                        stroke: new Stroke({ color: '#fff', width: 2 }),
                    }) : undefined,
                });
            },
        });

        const clusterSource = new ClusterSource({
            distance: 50,
            source: beneficiariosSourceRef.current,
        });

        const clusterLayer = new VectorLayer({
            source: clusterSource,
            style: (clusterFeature) => {
                const features = clusterFeature.get('features');
                const size = features.length;
                const first = features[0];
                const areaFilterVal = areaFilterRef.current;
                const isDimmed = areaFilterVal !== 'todas' && first && first.get('__area') !== areaFilterVal;
                if (size === 1) {
                    if (isDimmed) return dimBeneficiariosStyle;
                    return new Style({
                        image: new Circle({
                            radius: 7,
                            fill: new Fill({ color: '#2196F3' }),
                            stroke: new Stroke({ color: '#fff', width: 2 }),
                        }),
                    });
                }
                return new Style({
                    image: new Circle({
                        radius: Math.min(18, 8 + size * 2),
                        fill: new Fill({ color: isDimmed ? '#ccc' : '#2196F3' }),
                        stroke: new Stroke({ color: '#fff', width: 2 }),
                    }),
                    text: new Text({
                        text: String(size),
                        fill: new Fill({ color: '#fff' }),
                        font: 'bold 11px sans-serif',
                    }),
                });
            },
        });

        const initialMap = new Map({
            target: mapRef.current,
            layers: [
                new TileLayer({ source: new OSM() }),
                clusterLayer,
                geometriasLayer,
            ],
            view: new View({
                center: [-60.50, -36.67],
                zoom: 7,
            }),
            controls: defaultControls({ attribution: false, zoom: true }),
        });

        initialMap.addOverlay(overlay);
        overlayRef.current = overlay;
        setMap(initialMap);

        initialMap.on('click', (evt) => {
            const feature = initialMap.forEachFeatureAtPixel(evt.pixel, (feat) => feat);
            if (feature) {
                const clusterFeats = feature.get('features');
                if (clusterFeats && clusterFeats.length > 1) {
                    const first = clusterFeats[0];
                    const count = clusterFeats.length;
                    const html = `
                        <div class="text-sm">
                            <div class="font-semibold text-base mb-1">${first.get('nombre_obra') || 'Microcréditos'}</div>
                            <div class="text-gray-600"><span class="font-medium">Proyecto:</span> ${first.get('obra_id') || '-'}</div>
                            <div class="text-gray-600"><span class="font-medium">Beneficiarios:</span> ${count}</div>
                            <div class="text-gray-600"><span class="font-medium">Partido:</span> ${first.get('partido_nombre') || first.get('partido') || '-'}</div>
                        </div>
                    `;
                    popupContentRef.current.innerHTML = html;
                    overlay.setPosition(evt.coordinate);
                } else {
                    const src = clusterFeats ? clusterFeats[0] : feature;
                    const props = src.getProperties();
                    const html = `
                        <div class="text-sm">
                            <div class="font-semibold text-base mb-1">${props.nombre_obra || 'Sin nombre'}</div>
                            <div class="text-gray-600"><span class="font-medium">ID:</span> ${props.obra_id || '-'}</div>
                            <div class="text-gray-600"><span class="font-medium">Área:</span> ${props.area_responsable || '-'}</div>
                            <div class="text-gray-600"><span class="font-medium">Estado:</span> ${props.estado || '-'}</div>
                            <div class="text-gray-600"><span class="font-medium">Partido:</span> ${props.partido_nombre || props.partido || '-'}</div>
                            ${props.beneficiario_nombre ? `<div class="text-gray-600"><span class="font-medium">Beneficiario:</span> ${props.beneficiario_nombre} ${props.beneficiario_apellido || ''}</div>` : ''}
                            ${props.source === 'geometrias_obras' ? `<div class="text-gray-600"><span class="font-medium">Tipo:</span> ${props.tipo_geometria || '-'}</div>` : ''}
                        </div>
                    `;
                    popupContentRef.current.innerHTML = html;
                    overlay.setPosition(evt.coordinate);
                }
            } else {
                overlay.setPosition(undefined);
            }
        });

        initialMap.on('pointermove', (evt) => {
            const hit = initialMap.hasFeatureAtPixel(evt.pixel);
            initialMap.getTargetElement().style.cursor = hit ? 'pointer' : '';
        });

        return () => {
            initialMap.setTarget(undefined);
        };
    }, []);

    useEffect(() => {
        if (!map) return;
        geometriasSourceRef.current.clear();
        beneficiariosSourceRef.current.clear();

        const geojsonFormat = new GeoJSON();
        features.forEach(f => {
            try {
                const feature = geojsonFormat.readFeature(f, {
                    featureProjection: 'EPSG:4326',
                    dataProjection: 'EPSG:4326',
                });
                feature.setProperties(f.properties || {});
                feature.set('__area', f.properties?.area_responsable || '');
                if (f.properties?.source === 'microcreditos_geom') {
                    beneficiariosSourceRef.current.addFeature(feature);
                } else {
                    geometriasSourceRef.current.addFeature(feature);
                }
            } catch (e) {
                console.warn('Error parsing feature:', e);
            }
        });

        geometriasSourceRef.current.changed();
        beneficiariosSourceRef.current.changed();

        const extents = [];
        if (geometriasSourceRef.current.getFeatures().length > 0) extents.push(geometriasSourceRef.current.getExtent());
        if (beneficiariosSourceRef.current.getFeatures().length > 0) extents.push(beneficiariosSourceRef.current.getExtent());
        if (extents.length > 0) {
            map.getView().fit([
                Math.min(...extents.map(e => e[0])),
                Math.min(...extents.map(e => e[1])),
                Math.max(...extents.map(e => e[2])),
                Math.max(...extents.map(e => e[3])),
            ], { padding: [50, 50, 50, 50], duration: 500, maxZoom: 16 });
        }
    }, [map, features, areaFilter]);

    const toggleLayer = (key) => {
        setLayerVisibility(prev => {
            const next = { ...prev, [key]: !prev[key] };
            if (key === 'geometrias') map?.getLayers().getArray().find(l => l.getSource() === geometriasSourceRef.current)?.setVisible(next.geometrias);
            if (key === 'beneficiarios') map?.getLayers().getArray().find(l => l.getSource() instanceof ClusterSource)?.setVisible(next.beneficiarios);
            return next;
        });
    };

    return (
        <div className="h-screen w-full flex flex-col">
            <div className="bg-white border-b border-gray-200 px-4 py-2 flex items-center gap-4 shrink-0">
                <h2 className="text-lg font-semibold text-gray-800">Mapa de Proyectos</h2>
                <select
                    value={areaFilter}
                    onChange={(e) => setAreaFilter(e.target.value)}
                    className="border border-gray-300 rounded px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                    {AREAS_RESPONSABLES.map(a => (
                        <option key={a.value} value={a.value}>{a.label}</option>
                    ))}
                </select>
                <div className="relative flex-1 max-w-xs">
                    <input
                        ref={searchInputRef}
                        type="text"
                        placeholder="Buscar por expediente..."
                        onKeyDown={handleSearchKeyDown}
                        className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 pl-8"
                    />
                    <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                </div>
                {loading && <span className="text-sm text-gray-500 shrink-0">Cargando...</span>}
                <span className="text-sm text-gray-500 shrink-0">
                    {features.length} proyectos mapeados
                </span>
            </div>

            <div className="flex-1 relative min-h-0">
                <div ref={mapRef} className="w-full h-full" />

                <div className="absolute top-2 left-2 z-10 bg-white bg-opacity-95 rounded-lg shadow-lg p-3 max-w-[200px]">
                    <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">Capas</h3>
                    <div className="space-y-1.5">
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                            <input type="checkbox" checked={layerVisibility.geometrias} onChange={() => toggleLayer('geometrias')} className="rounded" />
                            <span className="w-3 h-3 rounded-sm inline-block shrink-0" style={{ backgroundColor: '#e6194B' }} />
                            <span className="truncate">Geometrías</span>
                        </label>
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                            <input type="checkbox" checked={layerVisibility.beneficiarios} onChange={() => toggleLayer('beneficiarios')} className="rounded" />
                            <span className="w-3 h-3 rounded-sm inline-block shrink-0" style={{ backgroundColor: '#2196F3' }} />
                            <span className="truncate">Beneficiarios</span>
                        </label>
                    </div>
                </div>

                <div className="absolute bottom-2 left-2 bg-white bg-opacity-95 rounded-lg shadow-lg p-3 z-10">
                    <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">Leyenda</h3>
                    <div className="space-y-1 text-xs text-gray-700">
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-sm shrink-0" style={{ backgroundColor: '#e6194B' }} />
                            <span>Geometrías de obras</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: '#2196F3' }} />
                            <span>Beneficiarios Microcréditos</span>
                        </div>
                        {areaFilter !== 'todas' && (
                            <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-sm shrink-0" style={{ backgroundColor: '#ccc' }} />
                                <span className="text-gray-400">otras áreas (atenuado)</span>
                            </div>
                        )}
                    </div>
                </div>

                <div ref={popupRef} className="absolute z-50 bg-white rounded-lg shadow-lg border border-gray-200 px-3 py-2 pointer-events-none min-w-[200px] max-w-[300px]" style={{ visibility: 'hidden' }}>
                    <div ref={popupContentRef}></div>
                </div>
            </div>
        </div>
    );
};

export default MapasPage;
